import { useState, useEffect } from "react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Hero } from "@/components/layout/Hero";
import { ProductCard } from "@/components/product/ProductCard";
// Flash Sales disabled for initial launch
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Star, Instagram, Award, Sparkles, MapPin, ShieldCheck, Heart } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { motion, useScroll, useTransform } from "framer-motion";
import { cn } from "@/lib/utils";
import { SEOHead } from "@/components/SEOHead";
import { useSiteContents, getContentWithFallback } from "@/hooks/useSiteContent";

interface Product {
  id: string;
  name: string;
  slug: string;
  price: number;
  original_price: number | null;
  images: string[];
  is_new: boolean;
  is_featured: boolean;
  category_id: string | null;
  category?: { name: string; slug: string } | null;
}

const featuredCollections = [
  {
    id: 1,
    title: "Bridal Couture",
    subtitle: "Sacred Traditions",
    image: "/images/heritage/collection-bridal.jpg",
    link: "/shop?category=lehengas",
    colSpan: "lg:col-span-8 lg:row-span-2",
  },
  {
    id: 2,
    title: "Heritage Silks",
    subtitle: "Timeless Drape",
    image: "/images/heritage/collection-silks.jpg",
    link: "/shop?category=sarees",
    colSpan: "lg:col-span-4 lg:row-span-1",
  },
  {
    id: 3,
    title: "The Suit Vault",
    subtitle: "Modern Cuts",
    image: "/images/heritage/collection-suit.jpg",
    link: "/shop?category=suits-sets",
    colSpan: "lg:col-span-4 lg:row-span-1",
  },
];

// Default icons for philosophies (icons can't be stored in CMS)
const philosophyIcons = [Award, ShieldCheck, MapPin, Heart];

const Index = () => {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [newArrivals, setNewArrivals] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const { scrollYProgress } = useScroll();
  const yParallax = useTransform(scrollYProgress, [0, 1], [0, -100]);

  // Fetch CMS content for homepage
  const { data: cmsContent } = useSiteContents(['homepage_hero', 'homepage_manifesto', 'homepage_newsletter', 'homepage_features', 'homepage_instagram', 'homepage_archives', 'homepage_instagram_images']);
  const manifestoContent = getContentWithFallback(cmsContent, 'homepage_manifesto');
  const featuresContent = getContentWithFallback(cmsContent, 'homepage_features');
  const instagramContent = getContentWithFallback(cmsContent, 'homepage_instagram');
  const archivesContent = getContentWithFallback(cmsContent, 'homepage_archives');
  const newsletterContent = getContentWithFallback(cmsContent, 'homepage_newsletter');
  const instagramImagesContent = getContentWithFallback(cmsContent, 'homepage_instagram_images');

  // Build Instagram images array from CMS
  const instagramImages = [
    instagramImagesContent.image1 || "/images/heritage/social-grid.jpg",
    instagramImagesContent.image2 || "/images/heritage/collection-silks.jpg",
    instagramImagesContent.image3 || "/images/heritage/collection-bridal.jpg",
    instagramImagesContent.image4 || "/images/heritage/collection-suit.jpg",
    instagramImagesContent.image5 || "/images/heritage/hero-charcoal.jpg",
    instagramImagesContent.image6 || "/images/heritage/hero-modern.jpg"
  ];

  // Build philosophies array from CMS content
  const philosophies = [
    { icon: philosophyIcons[0], title: featuresContent.feature1_title, desc: featuresContent.feature1_desc },
    { icon: philosophyIcons[1], title: featuresContent.feature2_title, desc: featuresContent.feature2_desc },
    { icon: philosophyIcons[2], title: featuresContent.feature3_title, desc: featuresContent.feature3_desc },
    { icon: philosophyIcons[3], title: featuresContent.feature4_title, desc: featuresContent.feature4_desc },
  ];

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      
      // Fetch featured products
      const { data: featured } = await supabase
        .from("products")
        .select("*")
        .eq("is_featured", true)
        .limit(4);
      
      if (featured) setFeaturedProducts(featured as Product[]);

      // Fetch new arrivals
      const { data: arrivals } = await supabase
        .from("products")
        .select("*")
        .eq("is_new", true)
        .limit(4);
      
      if (arrivals) setNewArrivals(arrivals as Product[]);
      
      setLoading(false);
    };

    fetchProducts();
  }, []);

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Leestone",
    "url": "https://leestonefashion.com",
    "logo": "https://leestonefashion.com/logo.png",
    "sameAs": [
      "https://www.instagram.com/leestone_45/"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+91-7736930840",
      "contactType": "customer service"
    }
  };

  return (
    <div className="min-h-screen bg-white font-body overflow-x-hidden">
      <SEOHead 
        title="Premium Ethnic Wear & Luxury Fashion" 
        description="Discover Leestone's curated collection of premium ethnic wear, bridal couture, and heritage silks. Handcrafted for the modern Indian."
        jsonLd={organizationSchema}
      />
      <Header />
      
      <main>
        <Hero />
        
        {/* The Boutique Philosophy */}
        <section className="py-24 border-b border-gold/5 bg-ivory/20">
          <div className="container-luxury grid grid-cols-1 md:grid-cols-4 gap-12 lg:gap-24">
            {philosophies.map((item, idx) => (
              <motion.div 
                key={idx} 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: idx * 0.1 }}
                className="flex flex-col items-center text-center group"
              >
                <div className="w-16 h-16 rounded-full bg-white border border-gold/10 flex items-center justify-center text-gold mb-6 shadow-sm group-hover:bg-gold group-hover:text-white transition-all duration-700 group-hover:scale-110">
                   <item.icon size={28} strokeWidth={1} />
                </div>
                <h4 className="font-display text-xl text-charcoal mb-3 italic">{item.title}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-[200px]">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Curated Museum Collections - Dynamic Grid */}
        <section className="py-32 bg-white">
          <div className="container-luxury">
            <div className="flex flex-col items-center text-center mb-24">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="flex items-center gap-4 mb-4"
              >
                 <div className="h-px w-8 bg-gold/30" />
                 <Sparkles size={14} className="text-gold" />
                 <div className="h-px w-8 bg-gold/30" />
              </motion.div>
              <span className="text-gold text-[10px] uppercase tracking-[0.5em] font-bold mb-4">Discovery Vault</span>
              <h2 className="font-display text-3xl md:text-4xl text-charcoal italic mb-4">The Heritage Archives</h2>
              <p className="text-muted-foreground font-light max-w-xl text-sm">Exploring the intersection of ancestral craftsmanship and contemporary silhouettes.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 auto-rows-[300px] md:auto-rows-[600px] gap-8">
              {featuredCollections.map((collection, idx) => (
                <motion.div
                  key={collection.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: idx * 0.2 }}
                  className={cn("relative overflow-hidden group cursor-pointer", collection.colSpan)}
                >
                  <Link to={collection.link} className="block w-full h-full">
                    {/* Gradient Overlay for Text Readability - Always visible at bottom */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-100 group-hover:opacity-90 transition-opacity duration-1000 z-10" />
                    
                    <img
                      src={collection.image}
                      alt={collection.title}
                      className="w-full h-full object-cover transition-transform duration-1000 ease-elegant group-hover:scale-105"
                    />
                    <div className="absolute bottom-6 left-6 right-6 md:bottom-12 md:left-12 md:right-12 z-20">
                      <p className="text-white/90 text-xs uppercase tracking-widest font-bold mb-4 translate-y-8 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-700 drop-shadow-md">
                        {collection.subtitle}
                      </p>
                      <h3 className="font-display text-3xl md:text-6xl text-white italic mb-6 leading-tight group-hover:text-gold transition-colors duration-700 drop-shadow-xl break-words pb-2 pr-4">{collection.title}</h3>
                      <div className="flex items-center gap-4 text-white group-hover:text-gold transition-colors duration-500 drop-shadow-lg">
                         <span className="text-xs uppercase tracking-widest font-bold">Explore Treasury</span>
                         <ArrowRight size={20} className="group-hover:translate-x-3 transition-transform duration-700" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Flash Sales disabled for initial launch */}

        {/* New Arrivals - The Silk Row */}
        <section className="py-32 bg-ivory/10 overflow-hidden">
          <div className="container-luxury">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-20">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <span className="text-gold text-[10px] uppercase tracking-[0.5em] font-bold mb-4 block underline underline-offset-8">{archivesContent.title || "Journal Updates"}</span>
                <h2 className="font-display text-3xl md:text-4xl text-charcoal italic">{archivesContent.subtitle || "The New Arrivals"}</h2>
              </motion.div>
              <Link to="/shop?sort=newest" className="group flex items-center gap-4 text-[11px] uppercase tracking-[0.3em] font-bold text-charcoal hover:text-gold transition-all mt-8 md:mt-0">
                Browse Entire Catalog
                <div className="w-12 h-12 bg-white border border-gold/10 rounded-full flex items-center justify-center transition-all group-hover:bg-gold group-hover:text-white">
                   <ArrowRight size={18} />
                </div>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
              {loading ? (
                [...Array(4)].map((_, i) => (
                  <div key={i} className="animate-pulse space-y-4">
                    <div className="bg-ivory aspect-[3/4] rounded-sm" />
                    <div className="h-4 bg-ivory w-3/4 mx-auto" />
                    <div className="h-4 bg-ivory w-1/2 mx-auto" />
                  </div>
                ))
              ) : (
                newArrivals.slice(0, 4).map((product, idx) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: idx * 0.1 }}
                  >
                    <ProductCard product={product} />
                  </motion.div>
                ))
              )}
            </div>
          </div>
        </section>

        {/* Immersive Narrative Parallax */}
        <section className="relative h-[90vh] min-h-[700px] overflow-hidden flex items-center justify-center text-center">
          <motion.div 
            style={{ 
              y: yParallax, 
              scale: 1.5, 
              backgroundImage: `url('${manifestoContent.image || "/images/heritage/manifesto-weaving.jpg"}')` 
            }}
            className="absolute inset-0 bg-cover bg-center brightness-75 grayscale-[30%]"
          />
          <div className="absolute inset-0 bg-charcoal/40 backdrop-blur-[1px]" />
          <div className="relative z-10 container-luxury max-w-5xl px-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2 }}
            >
              <span className="text-gold text-[10px] uppercase tracking-[0.5em] font-bold mb-8 block">{manifestoContent.title || "The Leestone Manifesto"}</span>
              <h2 className="font-display text-6xl md:text-8xl lg:text-9xl text-white mb-10 leading-[0.9] tracking-tighter">
                Crafting <br/><span className="italic pl-12 md:pl-24 text-gold">Eternity</span>
              </h2>
              <p className="text-white/80 text-lg md:text-2xl font-light leading-relaxed mb-16 max-w-3xl mx-auto italic">
                "{manifestoContent.quote || "Our journey began in the narrow lanes of Varanasi, celebrating the hands that craft magic. Leestone is more than a brand; it is a custodian of India's rich textile heritage."}"
              </p>
              <Link 
                to="/about" 
                className="inline-flex items-center gap-6 border border-white/30 text-white px-12 py-6 text-sm uppercase tracking-wider font-bold hover:bg-white hover:text-charcoal transition-all duration-700 backdrop-blur-md"
              >
                Read Our Heritage Story <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Curated Editor's Excellence */}
        <section className="py-32 bg-white">
          <div className="container-luxury">
            <div className="flex flex-col items-center text-center mb-24">
               <span className="text-gold text-[10px] uppercase tracking-[0.5em] font-bold mb-6 block">{newsletterContent?.title || "Season Selects"}</span>
               <h2 className="font-display text-3xl md:text-4xl text-charcoal italic">{newsletterContent?.subtitle || "The Editor's Choice"}</h2>
               <div className="w-12 h-px bg-gold/30 mt-8" />
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-12">
              {loading ? [...Array(4)].map((_, i) => <div key={i} className="h-[500px] bg-ivory animate-pulse" />) : 
                featuredProducts.map((product, idx) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: idx * 0.1 }}
                  >
                    <ProductCard product={product} />
                  </motion.div>
                ))
              }
            </div>
            
            <div className="mt-24 text-center">
               <Link to="/shop" className="text-sm uppercase tracking-wider font-bold text-charcoal border-b-2 border-gold/20 pb-2 hover:border-gold transition-all duration-500">
                  Enter The Full Boutique Portfolio
               </Link>
            </div>
          </div>
        </section>

        {/* The Social Narrative */}
        <section className="py-32 bg-ivory/20 overflow-hidden relative">
          <div className="absolute top-0 right-0 p-32 opacity-10 pointer-events-none">
             <h3 className="font-display text-[20vw] text-charcoal leading-none italic select-none">Heritage</h3>
          </div>
          
          <div className="container-luxury relative z-10 flex flex-col md:flex-row items-center justify-between gap-16">
            <div className="max-w-md text-center md:text-left">
               <Instagram className="w-10 h-10 mb-8 text-gold mx-auto md:mx-0" strokeWidth={1} />
               <span className="text-gold text-[10px] uppercase tracking-[0.5em] font-bold mb-4 block">{instagramContent.title || "Visual Diary"}</span>
               <h3 className="font-display text-5xl text-charcoal italic mb-6">{instagramContent.handle || "@leestone_45"}</h3>
               <p className="text-muted-foreground font-light mb-10 italic">{instagramContent.subtitle || "Follow our journey and share your Leestone moment."}</p>
               <a href={instagramContent.cta_link} target="_blank" rel="noopener noreferrer">
                 <Button className="bg-charcoal text-white hover:bg-gold h-14 px-12 uppercase tracking-[0.3em] text-[10px]">{instagramContent.cta_text || "Follow Our Story"}</Button>
               </a>
            </div>
            <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-6 w-full">
               {instagramImages.map((img, i) => (
                 <motion.div 
                   key={i} 
                   whileHover={{ scale: 0.95 }}
                   className={cn(
                     "aspect-square relative group overflow-hidden bg-ivory",
                     i === 2 && "hidden md:block"
                   )}
                 >
                   <img src={img} className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 transition-all duration-1000" alt="Instagram moment" />
                   <div className="absolute inset-0 bg-charcoal/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center text-white">
                     <Instagram size={24} strokeWidth={1} />
                   </div>
                 </motion.div>
               ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
